import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PillSpinner from '../components/PillSpinner';

const API_URL = 'http://localhost:5000/api';

// Helper: convert file to Base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const Prescriptions = () => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedPrescription, setExpandedPrescription] = useState(null);
  const [prescriptionFiles, setPrescriptionFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    patientId: '',
    doctorName: '',
    date: new Date().toISOString().split('T')[0],
    medicines: [{ medicineId: '', dosage: '', duration: '' }],
    status: 'Pending',
    notes: ''
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [presRes, custRes, medRes] = await Promise.all([
        axios.get(`${API_URL}/prescriptions`),
        axios.get(`${API_URL}/customers`),
        axios.get(`${API_URL}/medicines`)
      ]);
      setPrescriptions(presRes.data);
      setCustomers(custRes.data);
      setMedicines(medRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load files for a specific prescription from its own data (Base64 stored inside)
  const fetchFiles = async (prescriptionId) => {
    try {
      const res = await axios.get(`${API_URL}/prescriptions`);
      const pres = res.data.find(p => p._id === prescriptionId);
      if (pres && pres.fileData) {
        setPrescriptionFiles(prev => ({
          ...prev,
          [prescriptionId]: [{
            id: 'base64',
            filename: pres.fileName,
            data: pres.fileData,
            type: pres.fileType
          }]
        }));
      } else {
        setPrescriptionFiles(prev => ({ ...prev, [prescriptionId]: [] }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Upload file: convert to Base64 and update prescription
  const handleFileUpload = async (prescriptionId, file) => {
    if (!file) return;
    if (file.size > 500 * 1024) { // 500KB
    alert('File too large (max 500KB for prescription images)');
    return;
}
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      await axios.patch(`${API_URL}/prescriptions/${prescriptionId}`, {
        fileData: base64,
        fileName: file.name,
        fileType: file.type
      });
      alert('File attached!');
      fetchFiles(prescriptionId);
    } catch (err) {
      console.error(err);
      alert('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Delete file: remove Base64 fields from prescription
  const handleFileDelete = async (prescriptionId) => {
    if (window.confirm('Delete this file?')) {
      try {
        await axios.patch(`${API_URL}/prescriptions/${prescriptionId}`, {
          fileData: null,
          fileName: null,
          fileType: null
        });
        setPrescriptionFiles(prev => ({ ...prev, [prescriptionId]: [] }));
        alert('File removed');
      } catch (err) {
        console.error(err);
        alert('Delete failed');
      }
    }
  };

  const handleMedicineChange = (index, field, value) => {
    const updated = [...form.medicines];
    updated[index][field] = value;
    setForm({ ...form, medicines: updated });
  };

  const addMedicineRow = () => {
    setForm({
      ...form,
      medicines: [...form.medicines, { medicineId: '', dosage: '', duration: '' }]
    });
  };

  const removeMedicineRow = (index) => {
    const updated = form.medicines.filter((_, i) => i !== index);
    setForm({ ...form, medicines: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/prescriptions/${editingId}`, form);
        alert('Prescription updated');
      } else {
        await axios.post(`${API_URL}/prescriptions`, form);
        alert('Prescription added');
      }
      setShowForm(false);
      setEditingId(null);
      setForm({
        patientId: '',
        doctorName: '',
        date: new Date().toISOString().split('T')[0],
        medicines: [{ medicineId: '', dosage: '', duration: '' }],
        status: 'Pending',
        notes: ''
      });
      fetchAll();
    } catch (err) {
      alert('Error saving prescription');
    }
  };

  const handleEdit = (pres) => {
    setForm({
      patientId: pres.patientId,
      doctorName: pres.doctorName,
      date: pres.date.split('T')[0],
      medicines: pres.medicines,
      status: pres.status,
      notes: pres.notes || ''
    });
    setEditingId(pres._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this prescription?')) {
      await axios.delete(`${API_URL}/prescriptions/${id}`);
      fetchAll();
    }
  };

  const handleFill = async (pres) => {
    if (pres.status === 'Filled') {
      alert('Prescription already filled');
      return;
    }
    let canFill = true;
    for (const item of pres.medicines) {
      const medicine = medicines.find(m => m._id === item.medicineId);
      if (!medicine || medicine.stock < 1) {
        alert(`Insufficient stock for ${medicine?.name || 'medicine'}`);
        canFill = false;
        break;
      }
    }
    if (!canFill) return;
    for (const item of pres.medicines) {
      const medicine = medicines.find(m => m._id === item.medicineId);
      if (medicine) {
        await axios.patch(`${API_URL}/medicines/${medicine._id}/stock`, { stockChange: -1 });
      }
    }
    await axios.put(`${API_URL}/prescriptions/${pres._id}`, { ...pres, status: 'Filled' });
    fetchAll();
  };

  const toggleExpand = (presId) => {
    if (expandedPrescription === presId) {
      setExpandedPrescription(null);
    } else {
      setExpandedPrescription(presId);
      if (!prescriptionFiles[presId]) fetchFiles(presId);
    }
  };

  if (loading) return <PillSpinner />;

  const getPatientName = (id) => {
    const patient = customers.find(c => c._id === id);
    return patient ? patient.name : 'Unknown';
  };

  const getMedicineName = (id) => {
    const med = medicines.find(m => m._id === id);
    return med ? med.name : 'Unknown';
  };

  return (
    <div className="card">
      <div className="inventory-header">
        <h2>📋 Prescription Management</h2>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingId(null);
            setForm({
              patientId: '',
              doctorName: '',
              date: new Date().toISOString().split('T')[0],
              medicines: [{ medicineId: '', dosage: '', duration: '' }],
              status: 'Pending',
              notes: ''
            });
          }}
          className="btn-add"
        >
          {showForm ? 'Cancel' : '+ New Prescription'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="medicine-form" style={{ flexDirection: 'column' }}>
          <select value={form.patientId} onChange={e => setForm({ ...form, patientId: e.target.value })} required>
            <option value="">Select Patient</option>
            {customers.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
          <input type="text" placeholder="Doctor Name" value={form.doctorName} onChange={e => setForm({ ...form, doctorName: e.target.value })} required />
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required />
          <h4>Medicines Prescribed</h4>
          {form.medicines.map((item, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <select value={item.medicineId} onChange={e => handleMedicineChange(idx, 'medicineId', e.target.value)} required>
                <option value="">Select Medicine</option>
                {medicines.map(m => (
                  <option key={m._id} value={m._id}>{m.name}</option>
                ))}
              </select>
              <input type="text" placeholder="Dosage (e.g., 1 tab daily)" value={item.dosage} onChange={e => handleMedicineChange(idx, 'dosage', e.target.value)} required />
              <input type="text" placeholder="Duration (e.g., 7 days)" value={item.duration} onChange={e => handleMedicineChange(idx, 'duration', e.target.value)} required />
              <button type="button" onClick={() => removeMedicineRow(idx)} className="btn-restock" style={{ background: '#dc3545' }}>Remove</button>
            </div>
          ))}
          <button type="button" onClick={addMedicineRow} className="btn-add" style={{ background: '#6c757d' }}>+ Add Medicine</button>
          <textarea placeholder="Notes (optional)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows="2" style={{ padding: '8px', borderRadius: '8px' }} />
          <div>
            <button type="submit" className="btn-submit">{editingId ? 'Update' : 'Save'} Prescription</button>
          </div>
        </form>
      )}

      <table className="inventory-table prescriptions-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Patient</th>
            <th>Doctor</th>
            <th>Medicines</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {prescriptions.map(pres => (
            <React.Fragment key={pres._id}>
              <tr>
                <td>{new Date(pres.date).toLocaleDateString()}</td>
                <td>{getPatientName(pres.patientId)}</td>
                <td>{pres.doctorName}</td>
                <td>
                  {pres.medicines.map((item, idx) => (
                    <div key={idx}>{getMedicineName(item.medicineId)} – {item.dosage} for {item.duration}</div>
                  ))}
                </td>
                <td style={{ color: pres.status === 'Filled' ? 'green' : 'orange', fontWeight: 'bold' }}>{pres.status}</td>
                <td>
                  <button onClick={() => handleEdit(pres)} className="btn-edit">Edit</button>
                  <button onClick={() => handleDelete(pres._id)} className="btn-restock" style={{ background: '#dc3545' }}>Delete</button>
                  {pres.status !== 'Filled' && <button onClick={() => handleFill(pres)} className="btn-sale">Mark Filled</button>}
                  <button onClick={() => toggleExpand(pres._id)} className="btn-edit">Files</button>
                </td>
              </tr>
              {expandedPrescription === pres._id && (
  <td colSpan="6">
    <div className="prescription-files-expanded">
      <div><strong>📎 Attached Files:</strong></div>
      <div className="file-list">
        {prescriptionFiles[pres._id] && prescriptionFiles[pres._id].length > 0 ? (
          prescriptionFiles[pres._id].map(file => (
            <div key={file.id} className="file-item">
              {file.type?.startsWith('image/') ? (
                <a href={file.data} target="_blank" rel="noopener noreferrer">🖼️ View Image</a>
              ) : (
                <a href={file.data} download={file.filename}>📄 {file.filename}</a>
              )}
              <button onClick={() => handleFileDelete(pres._id)} className="btn-restock" style={{ background: '#dc3545', padding: '2px 8px' }}>Remove</button>
            </div>
          ))
        ) : (
          <span>No files attached.</span>
        )}
      </div>
      <div className="upload-area">
        <input
          type="file"
          accept="image/*,application/pdf"
          id={`file-upload-${pres._id}`}
          onChange={(e) => {
            if (e.target.files[0]) handleFileUpload(pres._id, e.target.files[0]);
          }}
          disabled={uploading}
        />
        {uploading && <span>⏳ Uploading...</span>}
      </div>
    </div>
  </td>
)}
            </React.Fragment>
          ))}
          {prescriptions.length === 0 && (
            <tr>
              <td colSpan="6" style={{ textAlign: 'center' }}>No prescriptions yet. Create one!</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Prescriptions;